import type {
  PriceFiltersQueryStringParams,
  PriceFiltersStateToQueryParamsParser,
} from '@/scopes/common/infrastructure/query-strings/types/priceFilters'
import { format, isValid, parseISO, subYears } from 'date-fns'

export interface BookingDetailsPriceFilters {
  participants: { birthDate: string }[]
  transportType: 'flight' | 'bus' | 'own-transport' | null
}

interface BookingDetailsPriceFiltersParserDependencies {
  today?: Date
}

const TRANSPORT_TYPE_MAP: Record<NonNullable<BookingDetailsPriceFilters['transportType']>, string> = {
  'flight': 'Flight',
  'bus': 'Bus',
  'own-transport': 'SelfDrive',
}

const DEFAULT_TRANSPORT_TYPE = 'SelfDrive'
const DEFAULT_PARTICIPANTS_COUNT = 2
const DEFAULT_PARTICIPANT_AGE = 30
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export function createBookingDetailsPriceFiltersParser(
  { today = new Date() }: BookingDetailsPriceFiltersParserDependencies = {},
): PriceFiltersStateToQueryParamsParser<BookingDetailsPriceFilters> {
  function serialize(state: BookingDetailsPriceFilters): PriceFiltersQueryStringParams {
    const birthDates = state.participants.map(participant => toISODate(participant.birthDate))
    const allBirthDatesAreValid = birthDates.length > 0 && birthDates.every(isValidISODate)

    return {
      Participants: allBirthDatesAreValid ? [birthDates] : [buildDefaultBirthDates(today)],
      TransportType: state.transportType ? TRANSPORT_TYPE_MAP[state.transportType] : DEFAULT_TRANSPORT_TYPE,
    }
  }

  return { serialize }
}

function toISODate(value: string): string {
  return value.slice(0, 10)
}

function isValidISODate(value: string): boolean {
  return ISO_DATE_PATTERN.test(value) && isValid(parseISO(value))
}

function buildDefaultBirthDates(today: Date): string[] {
  const defaultBirthDate = format(subYears(today, DEFAULT_PARTICIPANT_AGE), 'yyyy-MM-dd')

  return Array.from({ length: DEFAULT_PARTICIPANTS_COUNT }, () => defaultBirthDate)
}
